import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Compass, CheckCircle2, ArrowRight, FileText, MessageSquare, TrendingUp, Target } from 'lucide-react';
import { SEO, getBreadcrumbSchema } from '../../components/SEO';

export const CareerAdvisoryPage = () => {
  const { t, direction, language } = useLanguage();

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: language === 'ar' ? 'الرئيسية' : 'Home', url: '/' },
    { name: language === 'ar' ? 'الإرشاد المهني' : 'Career Advisory' }
  ]);

  const services = [
    {
      icon: FileText,
      title: 'CV & Resume Support',
      description: 'Professional CV writing, review, and optimization to make your application stand out.',
    },
    {
      icon: Compass,
      title: 'LinkedIn Profile Optimization',
      description: 'Enhance your LinkedIn presence to attract recruiters and opportunities.',
    },
    {
      icon: MessageSquare,
      title: 'Interview Coaching',
      description: 'Prepare for interviews with mock sessions, feedback, and proven techniques.',
    },
    {
      icon: TrendingUp,
      title: 'Career Transition Support',
      description: 'Navigate career changes with strategic planning and guidance.',
    },
    {
      icon: Target,
      title: 'Salary Negotiation',
      description: 'Maximize your compensation with expert negotiation strategies.',
    },
  ];

  const benefits = [
    'Personalized one-on-one coaching sessions',
    'Industry insights from experienced HR professionals',
    'Practical tools and templates',
    'Flexible scheduling for your convenience',
    'Confidential and supportive environment',
    'Follow-up support after sessions',
  ];

  return (
    <div className="min-h-screen" data-testid="career-advisory-page">
      <SEO
        title="Career Advisory Services - CV, Interview & Career Coaching"
        titleAr="خدمات الإرشاد المهني - السيرة الذاتية والمقابلات والتدريب المهني"
        description="WeDo career advisory services help professionals advance their careers. CV writing, LinkedIn optimization, interview coaching, salary negotiation, and career transition support."
        descriptionAr="تساعد خدمات الإرشاد المهني من ويدو المهنيين في تطوير مسيرتهم المهنية. كتابة السيرة الذاتية وتحسين LinkedIn والتحضير للمقابلات والتفاوض على الراتب ودعم الانتقال الوظيفي."
        keywords="career advisory, CV writing, interview coaching, salary negotiation, LinkedIn optimization, إرشاد مهني, كتابة سيرة ذاتية, تحضير للمقابلات"
        canonicalUrl="/services/career-advisory"
        structuredData={breadcrumbSchema}
        language={language}
      />
      {/* Hero Section */}
      <section className="bg-white border-b border-[#E5E7EB] py-20">
        <div className="container-custom">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#111827] mb-6">
              {t('services.career.title')}
            </h1>
            <p className="text-xl text-[#6B7280] mb-8">
              {t('services.career.description')}
            </p>
            <Link to="/contact">
              <Button size="lg" className="btn-primary" data-testid="get-support-btn">
                {t('services.career.cta')}
                <ArrowRight className={`ms-2 h-5 w-5 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-[#F9FAFB]">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#111827] mb-4">
              Our Career Advisory Services
            </h2>
            <p className="text-lg text-[#6B7280] max-w-2xl mx-auto">
              Comprehensive support to help you achieve your career goals
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="card-hover" data-testid={`service-${index}`}>
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-xl bg-[#33AADD]/10 flex items-center justify-center mb-5">
                    <service.icon className="h-7 w-7 text-[#33AADD]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#111827] mb-3">{service.title}</h3>
                  <p className="text-[#6B7280]">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-[#111827] mb-6 text-center">
              Why Choose Our Career Advisory?
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-[#F9FAFB] rounded-lg" data-testid={`benefit-${index}`}>
                  <CheckCircle2 className="h-5 w-5 text-[#33AADD] flex-shrink-0 mt-0.5" />
                  <span className="text-[#6B7280]">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#33AADD]">
        <div className="container-custom text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Take the Next Step?
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Let us help you navigate your career journey with confidence.
          </p>
          <Link to="/contact">
            <Button size="lg" variant="secondary" className="bg-white text-[#33AADD] hover:bg-white/90" data-testid="cta-btn">
              Book a Session
              <ArrowRight className={`ms-2 h-5 w-5 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
            </Button>
          </Link>
        </div>
      </section>

      {/* Jobs CTA */}
      <section className="section-padding bg-[#F9FAFB]">
        <div className="container-custom text-center">
          <h2 className="text-2xl font-bold text-[#111827] mb-4">
            Looking for Job Opportunities?
          </h2>
          <p className="text-[#6B7280] mb-6">
            Browse our current openings and find your next role.
          </p>
          <Link to="/jobs">
            <Button variant="outline" className="btn-secondary">
              {t('nav.jobs')}
              <ArrowRight className={`ms-2 h-4 w-4 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CareerAdvisoryPage;
